import { type DefaultMessagesResponse } from '@post-office/message-maker-browser/types';

import {
	ROVO_EXTENSION_CHAT_INPUT_CTA_EXPERIMENT,
	ROVO_EXTENSION_CHAT_INPUT_MESSAGE_TEMPLATE_ID,
	ROVO_EXTENSION_CHAT_INPUT_CTA_CONFLUENCE_EXPERIMENT,
} from './constants';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

interface ExperimentalMessage {
	messageTemplateId: string;
	statsigExperiment: string;
	statsigParameterName: string;
	/**
	 * The default value to use when Statsig has not assigned a value for this
	 * experiment parameter. Must match the type of the experiment parameter.
	 *
	 * Defaults to `false` if omitted.
	 */
	statsigDefaultValue?: boolean | string;
	statsigTestCohortPredicate: (experimentValue: boolean | string) => boolean;
	/**
	 * Optional environment / client predicate that determines if this message
	 * should proceed to Statsig evaluation (and have exposure recorded)
	 * in the current context.
	 *
	 * If omitted, the message always proceeds to Statsig evaluation.
	 */
	shouldProceedToStatsigEvaluation?: () => boolean;
}

const isChromeBrowser = (): boolean => {
	if (typeof navigator === 'undefined') {
		return false;
	}

	const userAgent = navigator.userAgent || '';
	return userAgent.indexOf('Chrome') >= 0;
};

export const EXPERIMENTAL_MESSAGES: ExperimentalMessage[] = [
	{
		// https://switcheroo.atlassian.com/ui/gates/339bb9dc-b17b-40aa-af80-9b37a4e98c58/key/rovo-ext_chat_input_rovo_extension_cta
		messageTemplateId: ROVO_EXTENSION_CHAT_INPUT_MESSAGE_TEMPLATE_ID,
		statsigExperiment: ROVO_EXTENSION_CHAT_INPUT_CTA_EXPERIMENT,
		statsigParameterName: 'isEnabled',
		statsigTestCohortPredicate: (experimentValue) => experimentValue === true,
		shouldProceedToStatsigEvaluation: isChromeBrowser,
	},
	{
		// https://switcheroo.atlassian.com/ui/gates/d6b8611c-3b34-4296-bf30-83ff802a8826/key/rovo-ext_chat_input_rovo_extension_cta-confluence
		messageTemplateId: ROVO_EXTENSION_CHAT_INPUT_MESSAGE_TEMPLATE_ID,
		statsigExperiment: ROVO_EXTENSION_CHAT_INPUT_CTA_CONFLUENCE_EXPERIMENT,
		statsigParameterName: 'isEnabled',
		statsigTestCohortPredicate: (experimentValue) => experimentValue === true,
		shouldProceedToStatsigEvaluation: isChromeBrowser,
	},
];

export const filterExperimentalMessages = (
	messages: DefaultMessagesResponse['messages'],
): DefaultMessagesResponse['messages'] => {
	return messages.filter((message) => {
		const experimentalMessages = EXPERIMENTAL_MESSAGES.filter(
			(msg) => msg.messageTemplateId === message.messageTemplateId,
		);

		// Non-experimental messages pass through
		if (experimentalMessages.length === 0) {
			return true;
		}

		// Message passes if any matching experiment says the user is in cohort
		const passesAny = experimentalMessages.some((experimentalMessage) => {
			if (
				experimentalMessage.shouldProceedToStatsigEvaluation &&
				!experimentalMessage.shouldProceedToStatsigEvaluation()
			) {
				return false;
			}

			const defaultValue = experimentalMessage.statsigDefaultValue ?? false;
			const experimentValue = FeatureGates.getExperimentValue(
				experimentalMessage.statsigExperiment,
				experimentalMessage.statsigParameterName,
				defaultValue,
			);

			return experimentalMessage.statsigTestCohortPredicate(experimentValue);
		});

		return passesAny;
	});
};
